import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams as useParamsHook } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { type Action, type ActionCreator } from "@reduxjs/toolkit";
import { getDownloadURL, ref } from "firebase/storage";
import { type Unsubscribe, useBooleanState } from "react-better-html";

import { sse } from "../constants/sse";

import { type SSEConfig } from "../types/sse";

import { getProfileImage } from "./dicebear";
import { storageUserProfileImageRef } from "./firebase";
import { type SSEConnectionStatus, type SSEStreamController } from "./sse";

import { type RootState } from "../redux/store";
import { rootReduxActions, type RootReduxActions } from "../redux/rootSlices";

export function useReduxState<T>(selector: (state: RootState) => T): T {
   return useSelector<RootState, T>(selector);
}

export function useReduxAction<T extends ActionCreator<Action>>(actionCreator: (actions: RootReduxActions) => T): T {
   const dispatch = useDispatch();

   const ref = useRef<T | null>(null);

   if (ref.current === null) {
      ref.current = function wrappedReduxAction(...args: Parameters<T>) {
         return dispatch(actionCreator(rootReduxActions)(...args));
      } as T;
   }

   return ref.current;
}

type SSEStreamContext<EndpointName extends keyof SSEConfig> = {
   status: SSEConnectionStatus;
   lastData: SSEConfig[EndpointName]["data"] | undefined;
   disconnect: () => void;
};

type SSEStreamHandlers<EndpointName extends keyof SSEConfig> = {
   onMessage?: (data: SSEConfig[EndpointName]["data"], context: SSEStreamContext<EndpointName>) => void;
   onError?: (error: unknown, context: SSEStreamContext<EndpointName>) => void;
   onOpen?: (context: SSEStreamContext<EndpointName>) => void;
   onClose?: (context: SSEStreamContext<EndpointName>) => void;
};

type SSEStartListenerOptions<EndpointName extends keyof SSEConfig> = {
   pathParams?: SSEConfig[EndpointName] extends { path: (string | number)[] }
      ? SSEConfig[EndpointName]["path"]
      : undefined;
} & SSEStreamHandlers<EndpointName>;

type SSEStartListener = <EndpointName extends keyof SSEConfig>(
   endpointName: EndpointName,
   options: SSEStartListenerOptions<EndpointName>,
) => Unsubscribe;

export function useSSEStream() {
   const [status, setStatus] = useState<SSEConnectionStatus>("idle");
   const [lastData, setLastData] = useState<SSEConfig[keyof SSEConfig]["data"] | undefined>(undefined);

   const statusRef = useRef<SSEConnectionStatus>(status);
   const lastDataRef = useRef<SSEConfig[keyof SSEConfig]["data"] | undefined>(lastData);

   const streamRef = useRef<SSEStreamController | undefined>(undefined);
   const handlersRef = useRef<SSEStreamHandlers<keyof SSEConfig> | undefined>(undefined);

   const disconnect = useCallback(() => {
      streamRef.current?.close();
      streamRef.current = undefined;
      handlersRef.current = undefined;

      setStatus("idle");
      statusRef.current = "idle";
   }, []);

   const buildContext = useCallback(
      (): SSEStreamContext<keyof SSEConfig> => ({
         status: statusRef.current,
         lastData: lastDataRef.current,
         disconnect,
      }),
      [disconnect],
   );

   const startListener = useMemo<SSEStartListener>(
      () => (endpointName, options) => {
         disconnect();

         handlersRef.current = options as SSEStreamHandlers<keyof SSEConfig>;

         setStatus("connecting");
         statusRef.current = "connecting";

         streamRef.current = sse(endpointName, {
            onOpen: () => {
               setStatus("open");
               statusRef.current = "open";

               handlersRef.current?.onOpen?.(buildContext());
            },
            onMessage: (data) => {
               setLastData(data);
               lastDataRef.current = data;

               handlersRef.current?.onMessage?.(data, buildContext());
            },
            onError: (error) => {
               setStatus("error");
               statusRef.current = "error";

               handlersRef.current?.onError?.(error, buildContext());
            },
            onClose: () => {
               setStatus("closed");
               statusRef.current = "closed";
               streamRef.current = undefined;

               handlersRef.current?.onClose?.(buildContext());
            },
            path: options.pathParams,
         });

         return () => {
            disconnect();
         };
      },
      [disconnect, buildContext],
   );

   useEffect(() => {
      return () => {
         disconnect();
      };
   }, [disconnect]);

   return {
      status,
      lastData,
      startListener,
   };
}

export function useFirebaseUserProfileImageExists() {
   const alreadyCheckedRef = useRef<boolean>(false);

   const firebaseUser = useReduxState((state) => state.user.firebaseUser);

   const [fileExists, setFileExists] = useBooleanState();

   const userProfileImageRef = useMemo(
      () => (firebaseUser ? ref(storageUserProfileImageRef, firebaseUser.uid) : undefined),
      [firebaseUser],
   );

   useEffect(() => {
      if (!userProfileImageRef) return;
      if (alreadyCheckedRef.current) return;

      setFileExists.setFalse();

      getDownloadURL(userProfileImageRef)
         .then(() => {
            setFileExists.setTrue();
            alreadyCheckedRef.current = true;
         })
         .catch((error: any) => {
            const code = error.code;

            if (code === "storage/object-not-found") setFileExists.setFalse();

            alreadyCheckedRef.current = true;
         });
   }, [userProfileImageRef]);

   return {
      fileExists,
      setFileExists,
   };
}

export function useParams<Params extends Record<string, string | number | undefined>>(): Record<
   keyof Params,
   Params[keyof Params] | undefined
> {
   const params = useParamsHook();

   const result = useMemo<Params>(() => {
      const readyValue = {} as Params;

      Object.entries(params).forEach(([key, value]) => {
         const keyName = key as keyof Params;
         const valueType = value as string;

         (readyValue as any)[keyName] = !isNaN(Number(valueType)) ? parseInt(valueType) : valueType;
      });

      return readyValue;
   }, [params]);

   return result;
}

export function useQuery<Query extends Record<string, string | undefined>>(): Record<
   keyof Query,
   Query[keyof Query] | undefined
> {
   const searchParams = new URLSearchParams(window.location.search);

   return Object.fromEntries(searchParams as any) as Record<keyof Query, Query[keyof Query] | undefined>;
}

export function useProfileImage(): string {
   const firebaseUser = useReduxState((state) => state.user.firebaseUser);

   return firebaseUser
      ? firebaseUser.photoURL
         ? firebaseUser.photoURL
         : getProfileImage(firebaseUser.uid)
      : getProfileImage("");
}

export function useSelectedOrganization() {
   const organizationsList = useReduxState((state) => state.organizations.list);
   const selectedOrganizationId = useReduxState((state) => state.organizations.selectedOrganizationId);

   const selectedOrganization = useMemo(
      () => organizationsList?.find((organization) => organization.id === selectedOrganizationId),
      [organizationsList, selectedOrganizationId],
   );

   return selectedOrganization;
}
